import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { LOCALE_STORAGE_KEY, LOCALES } from './constants';
import {
  getByPath,
  getDefaultLocaleFromNavigator,
  interpolate,
  normalizeStoredLocale,
} from './utils';
import ja from './locales/ja.json';
import zhTW from './locales/zh-TW.json';

const MESSAGES = {
  ja,
  'zh-TW': zhTW,
};

const I18nContext = createContext({
  locale: 'ja',
  setLocale: () => {},
  t: (key) => key,
});

function readInitialLocale() {
  try {
    const stored = normalizeStoredLocale(
      typeof localStorage !== 'undefined' ? localStorage.getItem(LOCALE_STORAGE_KEY) : null
    );
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  return getDefaultLocaleFromNavigator();
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(readInitialLocale);

  const setLocale = useCallback((next) => {
    if (!LOCALES.includes(next)) return;
    setLocaleState(next);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (key, vars) => {
      const messages = MESSAGES[locale];
      const raw = getByPath(messages, key);
      if (raw === undefined || typeof raw !== 'string') {
        return key;
      }
      return interpolate(raw, vars);
    },
    [locale]
  );

  useEffect(() => {
    document.documentElement.lang = locale === 'zh-TW' ? 'zh-TW' : 'ja';
  }, [locale]);

  useEffect(() => {
    document.title = t('app.document_title');
  }, [locale, t]);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
    }),
    [locale, setLocale, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
